import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  youhuiquan: {
    title: '优惠券 - 各大平台优惠券汇总 | 福利导航',
    description: '汇集淘宝、京东、拼多多等各大电商平台及品牌商家的最新优惠券信息，每日更新，购物更省钱。福利导航助您发现最全优惠福利。',
    keywords: '优惠券,电商优惠券,淘宝优惠券,京东优惠券,拼多多优惠券,购物省钱',
  },
  huiyuanyiquan: {
    title: '会员权益 - 各大平台会员福利对比 | 福利导航',
    description: '整合爱奇艺、腾讯视频、B站、京东PLUS等各大平台会员专属福利与权益，让你的会员身份价值最大化。',
    keywords: '会员权益,视频会员,电商会员,会员对比,会员福利',
  },
  shenghuobutie: {
    title: '生活补贴 - 政府补贴与生活保障金汇总 | 福利导航',
    description: '政府补贴、企业津贴、生活保障金、住房补贴、育儿补贴等实用生活补贴信息一网打尽，帮助您不错过每一项惠民政策。',
    keywords: '生活补贴,政府补贴,住房补贴,育儿补贴,生活保障金',
  },
  zhichangfuli: {
    title: '职场福利 - 员工福利与职业发展资源 | 福利导航',
    description: '职场人专属福利指南，涵盖五险一金、年假福利、培训补贴、考证报销、学历提升资助等职场核心福利信息。',
    keywords: '职场福利,员工福利,五险一金,培训补贴,考证报销',
  },
  jiuyuefuli: {
    title: '教育福利 - 助学贷款奖学金教育补贴 | 福利导航',
    description: '学生助学贷款、奖学金申请、教育补贴及免费学习资源汇总，助力每一位学子无忧求学。',
    keywords: '教育福利,助学贷款,奖学金,教育补贴,学生福利',
  },
  jiankangfuli: {
    title: '健康福利 - 体检优惠与医疗健康资源 | 福利导航',
    description: '体检优惠、疫苗接种、健康保险、医疗报销等医疗健康类福利资源，守护您和家人的健康。',
    keywords: '健康福利,体检优惠,疫苗接种,健康保险,医疗福利',
  },
  lvyoufuli: {
    title: '旅游福利 - 景区门票酒店出行优惠 | 福利导航',
    description: '全国景区门票减免、酒店特惠、出行补贴等旅游相关福利，让您的旅行更省钱更舒心。',
    keywords: '旅游福利,景区门票,酒店优惠,出行补贴,旅游省钱',
  },
  jinrongfuli: {
    title: '金融福利 - 信用卡权益与理财优惠 | 福利导航',
    description: '信用卡权益、贷款利率优惠、理财福利等金融服务类福利资源，让您的资金管理更高效。',
    keywords: '金融福利,信用卡权益,贷款优惠,理财福利,银行福利',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'youhuiquan');
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const meta = categoryMeta[activeSlug] || categoryMeta['youhuiquan'];
  const categoryArticles = articles.filter((a) => a.categorySlug === activeSlug);

  const sortedArticles = [...categoryArticles].sort((a, b) => {
    if (sortBy === 'latest') {
      return b.publishDate.localeCompare(a.publishDate);
    }
    return 0;
  });

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: activeCategory.name },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 福利导航`,
    description: meta.description,
    url: `https://fulidaohang.com/category/${activeSlug}`,
    inLanguage: 'zh-CN',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: sortedArticles.map((a, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `https://fulidaohang.com/article/${a.slug}`,
        name: a.title,
      })),
    },
  };

  const breadcrumbLD = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fulidaohang.com' },
      { '@type': 'ListItem', position: 2, name: activeCategory.name },
    ],
  };

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  return (
    <>
      <Navbar />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />
      <link rel="canonical" href={`https://fulidaohang.com/category/${activeSlug}`} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={`https://fulidaohang.com/category/${activeSlug}`} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLD) }} />

      <div className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="mb-3">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {activeCategory.name}
            </h1>
            <p className="text-foreground-600">{activeCategory.description}</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="w-full lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-20">
                <h3 className="font-heading text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-3">
                  全部分类
                </h3>
                <div className="flex flex-row lg:flex-col flex-wrap gap-1.5">
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        activeSlug === cat.slug
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                      }`}
                    >
                      <span className={`w-5 h-5 flex items-center justify-center`}>
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      {cat.name}
                      <span className="ml-auto text-xs text-foreground-400">{cat.articleCount}</span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <main className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-foreground-500">
                  共 <strong>{categoryArticles.length}</strong> 篇福利
                </p>
                <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === 'latest' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    最新
                  </button>
                  <button
                    onClick={() => setSortBy('popular')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === 'popular' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    最热
                  </button>
                </div>
              </div>

              {sortedArticles.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {sortedArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="flex flex-col sm:flex-row gap-4 p-4 md:p-5 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <div className="w-full sm:w-48 h-36 sm:h-32 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          title={article.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2 line-clamp-2 hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i> {article.publishDate}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line"></i> {article.readTime}
                          </span>
                          {article.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-foreground-500">该分类暂无文章，敬请期待</p>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}